/**
 * Email attachments — file upload/remove, drag-and-drop on sheets.
 * Works for both reply cards and compose forms via data-email-attachments container.
 * The container's `data-email-upload-url` overrides the pm_crm upload endpoint (acqs composers).
 *
 * Uses event delegation so it works across sheet open/close cycles.
 */

(function () {

    function getCsrfToken() {
        return window._cc && window._cc.getCsrfToken ? window._cc.getCsrfToken() : '';
    }

    function uploadFile(file, fileList) {
        var formData = new FormData();
        formData.append('file', file);

        // An acqs composer names its own staging endpoint; pm_crm forms use the default.
        var container = findAttachmentContainer(fileList);
        var url = (container && container.getAttribute('data-email-upload-url')) || '/pm-crm/emails/attachments/upload/';
        var xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.setRequestHeader('X-CSRFToken', getCsrfToken());
        xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300 && xhr.responseText.trim()) {
                fileList.insertAdjacentHTML('beforeend', xhr.responseText);
            } else if (window.SeeMe && SeeMe.toast) {
                // A 422 names the refusal (the acqs endpoint's size cap).
                var reason = xhr.status === 422 && xhr.responseText.trim();
                SeeMe.toast.error(reason || 'Upload failed');
            }
        };
        xhr.onerror = function () {
            if (window.SeeMe && SeeMe.toast) SeeMe.toast.error('Upload failed');
        };
        xhr.send(formData);
    }

    /** Find the nearest attachment container from an element inside it. */
    function findAttachmentContainer(el) {
        return el && el.closest('[data-email-attachments]');
    }

    function getFileInput(container) {
        return container && container.querySelector('input[type="file"]');
    }

    function getFileList(container) {
        return container && container.querySelector('[data-file-list]');
    }

    // CC toggle (reply form)
    document.body.addEventListener('click', function (e) {
        var btn = e.target.closest('.js-reply-cc-toggle');
        if (!btn) return;
        var row = document.getElementById('reply-cc-row');
        if (!row) return;
        row.style.display = row.style.display === 'none' ? '' : 'none';
        if (row.style.display !== 'none') row.querySelector('input').focus();
    });

    // ── Cc chip field (acqs composer, templates/acqs/_lead_email_form.html) ──
    // htmx fetches the suggestion rows into `[data-email-cc-results]` as you type
    // (acqs/_lead_email_cc_suggestions.html); the list shows only when it has rows.  A picked
    // row, or a typed address on Enter, comma or blur, becomes a PM CRM-style chip carrying a
    // hidden `cc` input.  The field never takes
    // the To it shows (`data-email-cc-to`), which a new email's To radios change.  The
    // server re-checks every Cc (acqs.services.email_conversation._check_recipients).

    var CC_ADDRESS = /^[^\s@,;<>"]+@[^\s@,;<>".]+(\.[^\s@,;<>".]+)+$/;

    function ccKey(address) { return (address || '').trim().toLowerCase(); }

    function ccParts(field) {
        return {
            input: field.querySelector('[data-email-cc-input]'),
            chips: field.querySelector('[data-email-cc-chips]'),
            error: field.querySelector('[data-email-cc-error]'),
            results: field.querySelector('[data-email-cc-results]'),
        };
    }

    function setCcError(field, message) {
        var parts = ccParts(field);
        parts.input.classList.toggle('is-invalid', !!message);
        parts.error.textContent = message || '';
    }

    function syncCcChips(chips) {
        chips.classList.toggle('d-none', !chips.children.length);
    }

    function setCcResultsOpen(field, open) {
        ccParts(field).results.classList.toggle('show', open);
    }

    // Empty the input and close the list.  The input event lets htmx replace any pending
    // search with one for '', which answers with no rows.
    function clearCcInput(input) {
        input.value = '';
        setCcResultsOpen(input.closest('[data-email-cc]'), false);
        input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    function pickCcOption(field, option) {
        var input = ccParts(field).input;
        setCcError(field, addEmailCcChip(field, option.getAttribute('data-cc-address'), option.getAttribute('data-cc-name')));
        clearCcInput(input);
        input.focus();
    }

    // Add one chip; returns an error message, or '' (a repeat is ignored).  `replyAll` marks it
    // `data-cc-reply-all`, as the server marks a reply's prefilled chips (same markup,
    // templates/acqs/_lead_email_cc_chip.html), so the Reply all checkbox can remove it.
    function addEmailCcChip(field, address, name, replyAll) {
        var chips = ccParts(field).chips;
        var key = ccKey(address);
        if (key === ccKey(field.getAttribute('data-email-cc-to'))) return address + ' is already the To.';
        var isRepeat = Array.prototype.some.call(chips.querySelectorAll('[data-cc-email]'), function (chip) {
            return ccKey(chip.getAttribute('data-cc-email')) === key;
        });
        if (isRepeat) return '';
        var chip = document.createElement('span');
        chip.className = 'chip chip-sm';
        chip.setAttribute('data-cc-email', address);
        if (replyAll) chip.setAttribute('data-cc-reply-all', '');
        var icon = document.createElement('i');
        icon.className = 'fa-solid fa-envelope fa-xs';
        chip.appendChild(icon);
        chip.appendChild(document.createTextNode(' ' + (name || address)));
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'cc';
        hidden.value = address;
        chip.appendChild(hidden);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'chip-dismiss';
        btn.setAttribute('data-email-cc-remove', '');
        btn.setAttribute('aria-label', 'Remove ' + address);
        btn.innerHTML = '<i class="fa-solid fa-xmark"></i>';
        chip.appendChild(btn);
        chips.appendChild(chip);
        syncCcChips(chips);
        return '';
    }

    // Turn the typed text into chips.  Every valid address is added; what is not stays in
    // the input with the field error.  Returns true when the input ends empty.
    function commitCcInput(field) {
        var input = ccParts(field).input;
        var errors = [];
        var left = [];
        input.value.split(/[,;\s]+/).forEach(function (token) {
            if (!token) return;
            var error = CC_ADDRESS.test(token)
                ? addEmailCcChip(field, token, '')
                : token + ' is not a valid email address.';
            if (error) { errors.push(error); left.push(token); }
        });
        setCcError(field, errors[0] || '');
        if (left.length) {
            input.value = left.join(', ');
            return false;
        }
        clearCcInput(input);
        return true;
    }

    document.body.addEventListener('keydown', function (e) {
        var input = e.target.closest && e.target.closest('[data-email-cc-input]');
        if (!input) return;
        var field = input.closest('[data-email-cc]');
        if (e.key === 'Escape') {
            if (ccParts(field).results.classList.contains('show')) {
                e.preventDefault();
                e.stopPropagation();
                setCcResultsOpen(field, false);
            }
            return;
        }
        if (e.key !== 'Enter' && e.key !== ',' && e.key !== ';') return;
        e.preventDefault();
        // Enter with the list open takes its first row (a match, or the Add row), but only when
        // the rows are for the text in the input now: typing on before the next debounced
        // answer leaves the last list open, and Enter then takes the typed text instead.
        var results = ccParts(field).results;
        var first = results.classList.contains('show') && results.getAttribute('data-cc-query') === input.value.trim()
            && results.querySelector('[data-email-cc-option]');
        if (e.key === 'Enter' && first) {
            pickCcOption(field, first);
            return;
        }
        commitCcInput(field);
    });

    // Any edit by the rep, down to an empty field, clears the error.  clearCcInput's own
    // synthetic input event (not trusted) leaves a pick's error in place.
    document.body.addEventListener('input', function (e) {
        var input = e.target.closest && e.target.closest('[data-email-cc-input]');
        if (input && e.isTrusted) setCcError(input.closest('[data-email-cc]'), '');
    });

    document.body.addEventListener('focusout', function (e) {
        var input = e.target.closest && e.target.closest('[data-email-cc-input]');
        if (!input) return;
        var field = input.closest('[data-email-cc]');
        setCcResultsOpen(field, false);
        // A malformed address stays in the input with its error until the next keystroke;
        // an empty field has nothing to complain about.
        if (input.value.trim()) commitCcInput(field);
        else setCcError(field, '');
    });

    // Rows landed: show the list only when it has rows and they are for the text in the input
    // now (a slower answer for older text is dropped).
    document.body.addEventListener('htmx:afterSwap', function (e) {
        var results = e.target.closest && e.target.closest('[data-email-cc-results]');
        if (!results) return;
        var field = results.closest('[data-email-cc]');
        var input = ccParts(field).input;
        var q = new URL(e.detail.xhr.responseURL).searchParams.get('q') || '';
        results.setAttribute('data-cc-query', q.trim());
        setCcResultsOpen(field, !!results.querySelector('[data-email-cc-option]') &&
            q.trim() === input.value.trim() && document.activeElement === input);
    });

    // Picking a suggestion must not blur the input first (the blur would commit the partial text).
    document.body.addEventListener('mousedown', function (e) {
        if (e.target.closest('[data-email-cc-results]')) e.preventDefault();
    });

    document.body.addEventListener('click', function (e) {
        var option = e.target.closest('[data-email-cc-option]');
        if (!option) return;
        e.preventDefault();
        pickCcOption(option.closest('[data-email-cc]'), option);
    });

    // The thread sheet footer's Reply all checkbox (acqs/_lead_email_thread_sheet.html), checked
    // on open with the thread's other participants already Cc chips.  Unchecking removes those
    // chips and leaves any the rep added; checking puts them back.
    document.body.addEventListener('change', function (e) {
        var box = e.target.closest('[data-email-reply-all]');
        if (!box) return;
        var form = document.getElementById(box.getAttribute('data-email-reply-all'));
        var field = form && form.querySelector('[data-email-cc]');
        if (!field) return;
        if (box.checked) {
            (box.getAttribute('data-email-reply-all-cc') || '').split(',').forEach(function (address) {
                if (address) addEmailCcChip(field, address, '', true);
            });
        } else {
            field.querySelectorAll('[data-cc-reply-all]').forEach(function (chip) { chip.remove(); });
        }
        syncCcChips(ccParts(field).chips);
        setCcError(field, '');
    });

    document.body.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-email-cc-remove]');
        if (!btn) return;
        var chips = btn.closest('[data-email-cc-chips]');
        btn.closest('[data-cc-email]').remove();
        syncCcChips(chips);
        setCcError(chips.closest('[data-email-cc]'), '');
    });

    // A new email's To changed: drop a chip that is the new one.  The suggestions read the
    // checked To on each request, so they offer the old To again by themselves.
    document.body.addEventListener('change', function (e) {
        if (e.target.name !== 'to' || e.target.type !== 'radio') return;
        var field = e.target.form && e.target.form.querySelector('[data-email-cc]');
        if (!field) return;
        var to = e.target.value;
        field.setAttribute('data-email-cc-to', to);
        field.querySelectorAll('[data-cc-email]').forEach(function (chip) {
            if (ccKey(chip.getAttribute('data-cc-email')) === ccKey(to)) chip.remove();
        });
        syncCcChips(ccParts(field).chips);
        setCcError(field, '');
    });

    // Send with typed text still in Cc: chip it first.  Text that is not an address holds the
    // send, keeps its inline error, and names it in an error toast, so it is never dropped or
    // sent silently.
    document.body.addEventListener('htmx:confirm', function (e) {
        var field = e.target.querySelector && e.target.tagName === 'FORM' && e.target.querySelector('[data-email-cc]');
        if (!field) return;
        var parts = ccParts(field);
        if (!parts.input.value.trim()) return;
        if (!commitCcInput(field)) {
            e.preventDefault();
            if (window.SeeMe && SeeMe.toast) SeeMe.toast.error('Not sent. Cc: ' + parts.error.textContent);
            parts.input.focus();
        }
    });

    // Attach button → toggle dropzone visibility
    document.body.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-email-attach-toggle]');
        if (!btn) return;
        var container = findAttachmentContainer(btn);
        var dropzone = container && container.querySelector('[data-email-dropzone]');
        if (dropzone) {
            dropzone.style.display = dropzone.style.display === 'none' ? '' : 'none';
        }
    });

    // Dropzone "browse" click → trigger file input
    document.body.addEventListener('click', function (e) {
        var browse = e.target.closest('.file-uploader-browse');
        if (!browse) return;
        var dropzone = browse.closest('[data-email-dropzone]');
        var fileInput = dropzone && dropzone.querySelector('input[type="file"]');
        if (fileInput) fileInput.click();
    });

    // Dropzone drag-and-drop
    document.body.addEventListener('dragover', function (e) {
        var dropzone = e.target.closest('.file-uploader-dropzone');
        if (dropzone) e.preventDefault();
    });

    document.body.addEventListener('drop', function (e) {
        var dropzoneEl = e.target.closest('.file-uploader-dropzone');
        if (!dropzoneEl) return;
        e.preventDefault();
        var container = findAttachmentContainer(dropzoneEl);
        var fileList = getFileList(container);
        if (!fileList) return;
        for (var i = 0; i < e.dataTransfer.files.length; i++) {
            uploadFile(e.dataTransfer.files[i], fileList);
        }
    });

    // File input change → upload each file
    document.body.addEventListener('change', function (e) {
        if (e.target.type !== 'file') return;
        var container = findAttachmentContainer(e.target);
        if (!container) return;
        var fileList = getFileList(container);
        if (!fileList) return;

        Array.from(e.target.files).forEach(function (file) {
            uploadFile(file, fileList);
        });

        e.target.value = '';
    });

    // Remove attachment chip
    document.body.addEventListener('click', function (e) {
        var btn = e.target.closest('.js-reply-attachment-remove');
        if (!btn) return;
        var url = btn.getAttribute('data-delete-url');
        if (!url) return;

        var xhr = new XMLHttpRequest();
        xhr.open('DELETE', url);
        xhr.setRequestHeader('X-CSRFToken', getCsrfToken());
        xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) {
                var chip = btn.closest('.chip');
                if (chip) chip.remove();
            }
        };
        xhr.send();
    });

    // Form reset → clear attachment list
    document.body.addEventListener('reset', function (e) {
        var container = findAttachmentContainer(e.target);
        if (!container) return;
        var fileList = getFileList(container);
        if (fileList) fileList.innerHTML = '';
    });

    // Sheet-wide drag-to-upload
    function initSheetDragUpload() {
        // Every sheet holding an attachment list, not just the first .sheet on
        // the page: a record page's rail panel is also a .sheet and comes first.
        document.querySelectorAll('.sheet [data-email-attachments]').forEach(function (container) {
            var sheet = container.closest('.sheet');
            if (sheet.hasAttribute('data-email-drag-init')) return;
            sheet.setAttribute('data-email-drag-init', '');
            initOneSheet(sheet);
        });
    }

    function initOneSheet(sheet) {
        var body = sheet.querySelector('.sheet__body');
        var dragCounter = 0;
        var overlay = null;

        function showOverlay() {
            if (!body || overlay) return;
            overlay = document.createElement('div');
            overlay.className = 'sheet__drag-overlay';
            body.prepend(overlay);
        }

        function hideOverlay() {
            if (overlay) {
                overlay.remove();
                overlay = null;
            }
        }

        sheet.addEventListener('dragenter', function (e) {
            if (!e.dataTransfer.types.includes('Files')) return;
            dragCounter++;
            showOverlay();
        });

        sheet.addEventListener('dragleave', function () {
            dragCounter--;
            if (dragCounter <= 0) {
                dragCounter = 0;
                hideOverlay();
            }
        });

        sheet.addEventListener('dragover', function (e) {
            if (e.dataTransfer.types.includes('Files')) {
                e.preventDefault();
            }
        });

        sheet.addEventListener('drop', function (e) {
            dragCounter = 0;
            hideOverlay();
            if (!e.dataTransfer.files.length) return;
            e.preventDefault();
            // Find the visible attachment container
            var containers = sheet.querySelectorAll('[data-email-attachments]');
            var target = null;
            for (var i = 0; i < containers.length; i++) {
                if (containers[i].offsetParent !== null) { target = containers[i]; break; }
            }
            var fileList = target && getFileList(target);
            if (!fileList) return;
            for (var j = 0; j < e.dataTransfer.files.length; j++) {
                uploadFile(e.dataTransfer.files[j], fileList);
            }
        });
    }

    // Re-init when sheet content changes (MutationObserver)
    var observer = new MutationObserver(function () {
        if (document.querySelector('[data-email-attachments]')) {
            initSheetDragUpload();
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // The acqs email rail panel (acqs/_lead_email_panel.html) swaps itself between the thread
    // list and a thread.  A click inside it (a thread row, All emails) shows the sheet skeleton
    // until the swap, since a thread's first open waits on Gmail.  The old content is hidden,
    // not removed, so the trigger stays connected for htmx's own events; a failed request shows
    // it again.  A live refresh (acqs-ws.js, issued on the panel itself) keeps its content,
    // and the panel reads `data-panel-view="loading"` meanwhile so that refresh, which only
    // replaces a list, leaves it alone and the click's own swap still lands.
    document.body.addEventListener('htmx:beforeRequest', function (e) {
        var panel = e.detail.target;
        var trigger = e.detail.elt;
        if (!panel || !panel.matches || !panel.matches('[data-email-panel]')) return;
        if (!trigger || trigger === panel || !panel.contains(trigger) || !window.rfSheet) return;
        var view = panel.getAttribute('data-panel-view');
        panel.setAttribute('data-panel-view', 'loading');
        var children = Array.prototype.slice.call(panel.children);
        children.forEach(function (child) { child.hidden = true; });
        var placeholder = document.createElement('div');
        placeholder.setAttribute('data-email-panel-skeleton', '');
        placeholder.innerHTML = window.rfSheet.skeleton;
        panel.prepend(placeholder);
        trigger.addEventListener('htmx:afterRequest', function (done) {
            if (done.detail.successful) return;
            placeholder.remove();
            children.forEach(function (child) { child.hidden = false; });
            panel.setAttribute('data-panel-view', view);
        }, { once: true });
    });

})();
